import React, { useEffect, useState } from 'react'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'
// @ts-ignore
import { Button } from 'dsm-react-lib'
// @ts-ignore
import Select from 'react-select'
import { IconX, IconLock } from '@tabler/icons-react'
import { ButtonSpinner } from '../../../styles/common'
import { selectThemeColors } from '../../../utility/Utils'
import { ArcRoleInfo } from '../Chatbot/agent/agentArcService'

type RoleOption = { label: string; value: string; group?: string; description?: string }

export type { ArcRoleInfo }

interface ArcRoleSelectionModalProps {
  isOpen: boolean
  toggle: () => void
  roles: ArcRoleInfo[]
  isLoading?: boolean
  onConfirm: (role: string) => void
}

const ArcRoleSelectionModal: React.FC<ArcRoleSelectionModalProps> = ({
  isOpen,
  toggle,
  roles,
  isLoading,
  onConfirm
}) => {
  const [selectedRole, setSelectedRole] = useState<RoleOption | null>(null)
  const roleOptions: RoleOption[] = roles.map(({ role, group, description }) => ({
    label: group ? `${role} - ${group}` : role,
    value: role,
    group,
    description
  }))

  // Reset the selection each time the modal is reopened for a (possibly different) agent
  useEffect(() => {
    if (isOpen) setSelectedRole(null)
  }, [isOpen])

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      centered
      contentClassName="theme-surface border theme-border-default overflow-hidden"
      data-testid="arc-role-selection-modal"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <ModalHeader
        className="bg-primary !justify-between !border-0 !flex !items-center"
        toggle={toggle}
        close={
          <IconX
            size={24}
            onClick={toggle}
            className="text-white transition-colors duration-300 ease-in-out hover:text-white cursor-pointer"
            data-testid="close-button"
          />
        }>
        <div className="flex items-center text-[var(--white)]">
          <IconLock size={20} className="me-1" />
          <span className="text-[19px] font-semibold leading-none">Select Access Role</span>
        </div>
      </ModalHeader>
      <ModalBody className="theme-surface !border-0 !py-1">
        <p className="theme-text-heading my-1">
          This agent has multiple access roles available. Select the role you want to request.
        </p>
        <div className="my-1">
          <Select
            placeholder="Select role..."
            theme={selectThemeColors}
            classNamePrefix="select"
            menuPlacement="auto"
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
            options={roleOptions}
            value={selectedRole}
            formatOptionLabel={(option: RoleOption, { context }: { context: 'menu' | 'value' }) =>
              context === 'value' ? (
                <div className="text-sm">{option.value}</div>
              ) : (
                <div>
                  <div className="text-sm">{option.label}</div>
                  {option.description && (
                    <div
                      className="text-xs theme-text-muted line-clamp-1"
                      title={option.description}>
                      {option.description}
                    </div>
                  )}
                </div>
              )
            }
            onChange={(selected: RoleOption | null) => setSelectedRole(selected)}
          />
        </div>
        <div className="d-flex justify-content-end gap-1 my-1">
          <Button
            label="Cancel"
            variant="outline"
            color="neutral"
            className="dsm-btn-neutral"
            onClick={toggle}
          />
          <Button
            label={isLoading ? 'Requesting' : 'Confirm'}
            icon={isLoading ? <ButtonSpinner color="var(--color-primary)" /> : undefined}
            color="primary"
            disabled={!selectedRole || isLoading}
            onClick={() => selectedRole && onConfirm(selectedRole.value)}
          />
        </div>
      </ModalBody>
    </Modal>
  )
}

export default ArcRoleSelectionModal
